import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, notFound } from "@tanstack/react-router"
import { fetchAuthSession } from "aws-amplify/auth"
import { SavedPlate } from "ipctg-inflow-types/InFlowDB"
import { AppUserPUTFailReason } from "ipctg-inflow-types/InFlowEnums"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { cleanEmptyParams } from "@/utilities/etc"
import { ExpectedError, GatedAppUserError } from "@/utilities/expectedErrors"
import { licensePlateValidationRule, requiredRule } from "@/utilities/form"
import { getAreaHierarchyIDs } from "@/utilities/getAreaHierarchyIDs"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { useScopedNavigate } from "@/utilities/hooks/useScopedRouting"
import { useTheme } from "@/utilities/hooks/useTheme"
import { normalizeLicensePlate } from "@/utilities/normalizers"

export const Route = createFileRoute("/_authenticated/account/vehicles/edit")({
  staticData: {
    pageName: "Edit Vehicle",
  },
  validateSearch: (
    search: Record<string, unknown>
  ): {
    plate: string
  } => {
    return cleanEmptyParams({
      plate: (search.plate as string) || "",
    })
  },
  beforeLoad: async ({ context, search }) => {
    const me = await context.queryClient.fetchQuery(meQueryOptions())
    if (
      !search.plate ||
      !me?.inflowUser.savedPlates.some((v) => v.plate === search.plate)
    ) {
      throw notFound()
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { plate } = Route.useSearch()
  return <VehicleEditRouteComponent plate={plate} />
}

export function VehicleEditRouteComponent({ plate }: { plate?: string }) {
  const { t } = useTranslation(["errors", "common"])
  const { setError } = useErrorModal()
  const scopedNavigate = useScopedNavigate()
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const queryClient = useQueryClient()
  const { locationInformation } = useTheme()
  const locationID = locationInformation?.locationID
  const vehicle = me?.inflowUser.savedPlates.find((v) => v.plate === plate)

  const form = useForm<SavedPlate>({
    defaultValues: {
      plate: vehicle?.plate || "",
      nickname: vehicle?.nickname || "",
      lastUsed: vehicle?.lastUsed || "",
      state: vehicle?.state || "XX",
    },
    mode: "onBlur",
  })

  const { mutate: editVehicle, isPending: isEditVehiclePending } = useMutation({
    mutationFn: editVehicleMutation,
    onSuccess,
    onError,
  })

  const { mutate: deleteVehicle, isPending: isDeleteVehiclePending } =
    useMutation({
      mutationFn: deleteVehicleMutation,
      onSuccess,
      onError: onVehicleDeleteError,
    })

  return (
    <Card>
      <Heading level={1} as={2}>
        {t("account.vehicles.editHeader", { ns: "common" })}
      </Heading>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset>
          <input type="hidden" {...form.register("state")} />
          <input type="hidden" {...form.register("lastUsed")} />
          <Controller<SavedPlate, "nickname">
            name="nickname"
            control={form.control}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField
                {...rest}
                onFocus={() => form.clearErrors("root")}
                isInvalid={invalid}
              >
                <Label>
                  {t("account.vehicles.nickname", { ns: "common" })}{" "}
                  <span className="optional">
                    ({t("common.optional", { ns: "common" })})
                  </span>
                </Label>
                <TextField.Input innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Controller<SavedPlate, "plate">
            name="plate"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              validate: (value) => {
                const normalized = normalizeLicensePlate(value)
                return licensePlateValidationRule(t)(normalized)
              },
            }}
            render={({
              field: { ref, onChange, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField
                {...rest}
                onChange={(v) => onChange(normalizeLicensePlate(v))}
                onFocus={() => form.clearErrors("root")}
                isInvalid={invalid}
              >
                <Label>{t("account.vehicles.plate", { ns: "common" })}</Label>
                <TextField.Input innerRef={ref} />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            buttonType="primary"
            type="submit"
            isPending={isEditVehiclePending}
          >
            {t("common.save", { ns: "common" })}
          </Button>
          <Button
            presentation="link"
            buttonType="danger"
            type="button"
            onPress={() => deleteVehicle()}
            isPending={isDeleteVehiclePending}
          >
            <Text as="span" className="semi-bold">
              {t("account.vehicles.removeVehicle", { ns: "common" })}
            </Text>
          </Button>
        </fieldset>
      </form>
    </Card>
  )

  function onSubmit(data: SavedPlate) {
    editVehicle(data)
  }

  async function editVehicleMutation(data: SavedPlate) {
    const newSavedPlates =
      me?.inflowUser.savedPlates.map((v) => (v.plate === plate ? data : v)) ||
      []
    return updateUser(newSavedPlates)
  }

  async function deleteVehicleMutation() {
    const newSavedPlates =
      me?.inflowUser.savedPlates.filter((v) => v.plate !== plate) || []

    return updateUser(newSavedPlates)
  }

  async function updateUser(newSavedPlates: SavedPlate[]) {
    const session = await fetchAuthSession()
    const cognitoIDToken = session.tokens?.idToken

    if (!cognitoIDToken || !me)
      throw new GatedAppUserError(AppUserPUTFailReason.Unauthorized)

    const rsp = await api.appUser.PUT({
      cognitoIDToken: cognitoIDToken.toString(),
      enterpriseID: locationID
        ? getAreaHierarchyIDs({ locationID }).enterpriseID
        : null,
      ...me.inflowUser,
      savedPlates: newSavedPlates,
    })

    if ("failReason" in rsp) {
      throw new GatedAppUserError(rsp.failReason)
    }

    return rsp
  }

  function onSuccess() {
    queryClient.invalidateQueries({
      ...meQueryOptions(),
      refetchType: "all",
    })
    scopedNavigate({ to: "/account/vehicles" })
  }

  function onError(error: Error) {
    if (error instanceof ExpectedError) {
      console.log(error)
      form.setError("root", { message: t(error.translationKey) })
    } else {
      form.setError("root", { message: error.message })
    }
  }

  function onVehicleDeleteError(error: Error) {
    setError({ error })
  }
}
